import { supabase } from '@/integrations/supabase/client';

export interface VenueProfile {
  id: string;
  user_id: string;
  venue_name: string;
  country: string;
  state_province: string;
  city: string;
  zip_code: string;
  address_line?: string;
  is_claimed: boolean;
  claimed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface VenueClaimRequest {
  id: string;
  requester_id: string;
  venue_name: string;
  country: string;
  state_province: string;
  city: string;
  zip_code: string;
  address_line?: string;
  status: string;
  payment_status: string;
  payment_amount: number;
  admin_notes?: string;
  created_at: string;
  updated_at: string;
  processed_at?: string;
  processed_by?: string;
}

export interface VenueLocation {
  venue_name: string;
  country: string;
  state_province: string;
  city: string;
  zip_code: string;
  address_line?: string;
}

export const venueClaimService = {
  // Get all available venues (both external and internal) - deduplicated
  async getAllAvailableVenues(): Promise<VenueProfile[]> {
    try {
      // Get external venue profiles
      const { data: externalVenues, error: externalError } = await supabase
        .from('venue_profiles')
        .select('*')
        .order('venue_name');
      
      if (externalError) throw externalError;

      // Get internal users who are venues
      const { data: internalVenues, error: internalError } = await supabase
        .from('internal_users')
        .select('id, username')
        .eq('role', 'venue')
        .eq('is_active', true)
        .order('username');
      
      if (internalError) throw internalError;

      // Create a Set of existing venue IDs from venue_profiles to avoid duplicates
      // Compare venue_profiles.id with internal_users.id (not user_id)
      const existingVenueIds = new Set((externalVenues || []).map(venue => venue.id));

      // Format internal venues to match VenueProfile interface, excluding duplicates
      const formattedInternalVenues: VenueProfile[] = (internalVenues || [])
        .filter(venue => {
          const isDuplicate = existingVenueIds.has(venue.id);
          if (isDuplicate) {
            console.log(`Filtering out duplicate venue: ${venue.username} (ID: ${venue.id}) - already exists in venue_profiles`);
          }
          return !isDuplicate; // Only include if not already in venue_profiles
        })
        .map(venue => ({
          id: venue.id,
          user_id: venue.id,
          venue_name: venue.username || 'Venue',
          country: 'United States',
          state_province: 'California', // Default state for internal venues
          city: 'San Francisco', // Default city for internal venues
          zip_code: '94102',
          address_line: '',
          is_claimed: true, // Internal venues are considered claimed
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }));

      // Combine and return deduplicated venues (venue_profiles take priority)
      return [...(externalVenues || []), ...formattedInternalVenues];
    } catch (error) {
      console.error('Error fetching venues:', error);
      throw error;
    }
  },

  // Get all registered venue profiles (legacy method for backward compatibility)
  async getVenueProfiles(): Promise<VenueProfile[]> {
    const { data, error } = await supabase
      .from('venue_profiles')
      .select('*')
      .order('venue_name');
    
    if (error) throw error;
    return data || [];
  },

  // Create a venue claim request
  async createClaimRequest(venueData: VenueLocation): Promise<VenueClaimRequest> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated');

    const { data, error } = await supabase
      .from('venue_claim_requests')
      .insert({
        requester_id: user.id,
        ...venueData
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Get user's claim requests
  async getUserClaimRequests(): Promise<VenueClaimRequest[]> {
    const { data, error } = await supabase
      .from('venue_claim_requests')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Check if a venue name/location combination exists
  async checkVenueExists(venueData: VenueLocation): Promise<VenueProfile | null> {
    const { data, error } = await supabase
      .from('venue_profiles')
      .select('*')
      .eq('venue_name', venueData.venue_name)
      .eq('city', venueData.city)
      .eq('state_province', venueData.state_province)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  // Create a venue profile (for unclaimed venues)
  async createVenueProfile(venueData: VenueLocation): Promise<VenueProfile> {
    const { data, error } = await supabase
      .from('venue_profiles')
      .insert({
        user_id: null, // Unclaimed venue
        is_claimed: false,
        ...venueData
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};

// Location data service
export const locationService = {
  getCountries(): string[] {
    return [
      'United States',
      'Canada',
      'United Kingdom',
      'Australia',
      'Germany',
      'France',
      'Italy',
      'Spain',
      'Mexico',
      'Brazil',
      'Other'
    ];
  },

  getStatesProvinces(country: string): string[] {
    const stateMap: { [key: string]: string[] } = {
      'United States': [
        'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
        'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
        'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
        'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
        'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
        'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
        'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
        'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
        'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
        'West Virginia', 'Wisconsin', 'Wyoming'
      ],
      'Canada': [
        'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick',
        'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia',
        'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan',
        'Yukon'
      ],
      'United Kingdom': [
        'England', 'Scotland', 'Wales', 'Northern Ireland'
      ],
      'Australia': [
        'New South Wales', 'Victoria', 'Queensland', 'Western Australia',
        'South Australia', 'Tasmania', 'Northern Territory',
        'Australian Capital Territory'
      ]
    };
    
    return stateMap[country] || ['Other'];
  },

  getCities(stateProvince: string): string[] {
    // For demo purposes, return common city names
    // In a real app, this would be a comprehensive database lookup
    return [
      'Select City', 'New York', 'Los Angeles', 'Chicago', 'Houston',
      'Phoenix', 'Philadelphia', 'San Antonio', 'San Diego', 'Dallas',
      'San Jose', 'Austin', 'Jacksonville', 'Fort Worth', 'Columbus',
      'Charlotte', 'San Francisco', 'Indianapolis', 'Seattle', 'Denver',
      'Washington', 'Boston', 'Other'
    ];
  }
};